import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/src';
import type { CanonicalId } from '@atlassian/jira-common-constants/src/project-types';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	STANDARD_EDITION,
	PREMIUM_EDITION,
	type ApplicationEdition,
} from '@atlassian/jira-shared-types/src/edition.tsx';

export const PREMIUM_FEATURES_COHORT_VARIANT = 'variant';
export const PREMIUM_FEATURES_COHORT_CONTROL = 'control';
export const PREMIUM_FEATURES_COHORT_NOT_ENROLLED = 'not-enrolled';

export function getDemonstratingTheValueofJiraFeaturesExperiment() {
	const [config, fire] = UNSAFE_noExposureExp('demonstrating_the_value_of_jira_premium_features');
	const cohort = config.get('cohort', PREMIUM_FEATURES_COHORT_NOT_ENROLLED);

	return {
		cohort,
		fire,
	};
}

type IsEligibleForDemonstratingTheValueOfJiraFeaturesExperimentArgs = {
	edition: string;
	productKey: string;
	inGracePeriod: boolean;
};

export function isEligibleForDemonstratingTheValueOfJiraFeaturesExperiment({
	edition,
	productKey,
	inGracePeriod,
}: IsEligibleForDemonstratingTheValueOfJiraFeaturesExperimentArgs) {
	if (!fg('demonstrating_value_of_jira_premium_features_gate')) {
		return false;
	}

	const isSupportedStandard = edition === STANDARD_EDITION;
	const isSupportedPremium =
		edition === PREMIUM_EDITION &&
		(productKey === 'jira' || productKey === 'jira-core' || productKey === 'jira-software');
	const isSupportedEdition = isSupportedStandard || isSupportedPremium;
	const isEligible = isSupportedEdition && !inGracePeriod;

	return isEligible;
}

export const getDemonstratingTheValueOfJiraFeaturesCohort = (
	args: IsEligibleForDemonstratingTheValueOfJiraFeaturesExperimentArgs,
): string => {
	if (!isEligibleForDemonstratingTheValueOfJiraFeaturesExperiment(args)) {
		return PREMIUM_FEATURES_COHORT_NOT_ENROLLED;
	}

	const cohort = expVal(
		'demonstrating_the_value_of_jira_premium_features',
		'cohort',
		'not-enrolled',
	);

	// This should always be either `not-enrolled` or `control` as variant should
	// not be running this code
	return cohort;
};

export const firePremiumFeaturesExperimentExposure = ({
	productKey,
	edition,
	inGracePeriod,
	createAnalyticsEvent,
	shouldFire,
}: {
	productKey: CanonicalId;
	edition: ApplicationEdition;
	inGracePeriod: boolean;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	shouldFire: boolean;
}): {
	cohort: string;
} => {
	const cohort = getDemonstratingTheValueOfJiraFeaturesCohort({
		productKey,
		edition,
		inGracePeriod,
	});

	// send GAS event
	if (cohort !== PREMIUM_FEATURES_COHORT_NOT_ENROLLED && shouldFire) {
		fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', {
			flagKey: 'demonstrating_the_value_of_jira_premium_features',
			value: cohort,
			growthInitiative: 'buy',
		});
	}

	return { cohort };
};
